import { useState, memo, useMemo, useEffect, useRef } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import {
  Box,
  Card,
  Typography,
  TextField,
  MenuItem,
  Button,
  Drawer,
} from "@mui/material"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers"
import useDeviceType from "../../../utils/useDeviceType"
import { getFullImageUrl } from "../../../utils/getFullImageUrl"

import { ReactComponent as PersonIcon } from "../../../assets/imgs/icons/person.svg"
import { ReactComponent as ClockIcon } from "../../../assets/imgs/icons/clock.svg"
import { ReactComponent as BuildingIcon } from "../../../assets/imgs/icons/building.svg"
import { ReactComponent as GreyLocationIcon } from "../../../assets/imgs/icons/location2.svg"

import {
  getActiveRoomsByTenantAndBranch,
  getBlockedDateByTenant,
  getAllBranchesByTenant,
  getSpecificBranchByTenant,
} from "../../../api/booking"

import {
  toISODate,
  toMMDDYYYY,
  hhmmToMinutes,
  parseRanges,
  isRangeOverlappingBlockedRange,
  getCalendarDayBookableWindows,
  buildTimeOptionsFromWindows,
} from "../../../utils/bookingSlotsUtils"

const titleSx = { color: "text.primary", fontWeight: 600, fontSize: "14px" }

const inputSx = {
  "& .MuiOutlinedInput-root": {
    "&.Mui-focused fieldset": {
      borderColor: "text.primary",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "text.primary",
  },
}

const createLabel = (Icon, text) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Icon sx={{ fontSize: 24, color: "text.secondary" }} />
    <Box component="span">{text}</Box>
  </Box>
)

const parseBookingDateValue = (
  value,
  fallbackMonth,
  fallbackDay,
  fallbackYear,
) => {
  if (!value && (!fallbackMonth || !fallbackDay || !fallbackYear)) return null

  if (value) {
    const nativeParsed = new Date(value)
    if (!Number.isNaN(nativeParsed.getTime())) return nativeParsed

    const parts = String(value).trim().split(/\s+/)
    if (parts.length === 3) {
      const [dayPart, monthPart, yearPart] = parts
      const rebuilt = new Date(`${monthPart} ${dayPart} ${yearPart}`)
      if (!Number.isNaN(rebuilt.getTime())) return rebuilt
    }
  }

  if (fallbackMonth && fallbackDay && fallbackYear) {
    const rebuilt = new Date(`${fallbackMonth} ${fallbackDay}, ${fallbackYear}`)
    if (!Number.isNaN(rebuilt.getTime())) return rebuilt
  }

  return null
}

const BookingField = memo(
  ({
    name,
    value,
    onChange,
    label,
    options,
    type = "text",
    endAdornment,
    disabled = false,
    error = false,
    htmlInputProps,
    onKeyDown,
    onPaste,
    selectProps,
  }) => (
    <TextField
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      label={label}
      select={!!options}
      fullWidth
      size="small"
      disabled={disabled}
      error={error}
      onKeyDown={onKeyDown}
      onPaste={onPaste}
      SelectProps={selectProps}
      sx={{
        ...inputSx,
        ...(disabled && {
          "& .MuiOutlinedInput-root.Mui-disabled": {
            backgroundColor: "#D9D9D9",
          },
        }),
      }}
      slotProps={{
        input: { endAdornment },
        htmlInput: htmlInputProps,
      }}
    >
      {options?.map((opt) => (
        <MenuItem
          key={opt.value}
          value={opt.value}
          disabled={Boolean(opt.disabled)}
          sx={{
            color: opt.disabled ? "text.subcaption" : "text.primary",
            opacity: opt.disabled ? 1 : undefined,
          }}
        >
          {opt.secondary ? (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box component="span">{opt.label}</Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  color: "text.subcaption",
                  fontSize: "12px",
                  whiteSpace: "nowrap",
                }}
              >
                <PersonIcon style={{ width: 14, height: 14 }} />
                <Box component="span">{opt.secondary}</Box>
              </Box>
            </Box>
          ) : (
            opt.label
          )}
        </MenuItem>
      ))}
    </TextField>
  ),
)

export default function BookingCard({
  tenant,
  hideDetails = false,
  onClose,
  onSave,
  selectedVenueId,
  onVenueChange,
  bookingData,
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const { isMobile } = useDeviceType()
  const isEditMode = hideDetails && Boolean(bookingData)

  const [rooms, setRooms] = useState([])
  const [branches, setBranches] = useState([])
  const [selectedBranchFromApi, setSelectedBranchFromApi] = useState(null)
  const [isLoadingBranchDetails, setIsLoadingBranchDetails] = useState(false)
  const [showVenueLayout, setShowVenueLayout] = useState(false)

  const [form, setForm] = useState({
    venue: "",
    room: "",
    date: null,
    time: "",
    guests: "",
    eventType: "",
    duration: "",
  })

  const [blockedRanges, setBlockedRanges] = useState([])
  const hasAppliedPrefillRef = useRef(false)
  const skipAutoTimeResetRef = useRef(false)
  const skipAutoTimeValidationRef = useRef(false)

  const tenantId = useMemo(
    () => tenant?.data?.id || sessionStorage.getItem("tenantId") || null,
    [tenant?.data?.id],
  )

  useEffect(() => {
    setBranches([])
    setRooms([])
    setBlockedRanges([])
    setForm((prev) => ({
      ...prev,
      venue: "",
      room: "",
      date: null,
      time: "",
      guests: "",
      eventType: "",
      duration: "",
    }))
  }, [tenantId])

  useEffect(() => {
    hasAppliedPrefillRef.current = false
  }, [bookingData])

  useEffect(() => {
    if (!tenantId) return
    let cancelled = false

    const loadBranches = async () => {
      try {
        const result = await getAllBranchesByTenant(tenantId)
        const branchList = Array.isArray(result) ? result : []
        if (cancelled) return
        setBranches(branchList)

        if (!branchList.length) {
          setForm((prev) => ({
            ...prev,
            venue: "",
            room: "",
            time: "",
            guests: "",
            duration: "",
          }))
          return
        }

        const requestedVenueId = String(
          selectedVenueId || bookingData?.venue?.id || "",
        )
        const requestedExists = branchList.some(
          (branch) => String(branch?.id) === requestedVenueId,
        )
        const nextVenue = requestedExists
          ? requestedVenueId
          : String(branchList[0].id)

        setForm((prev) => ({
          ...prev,
          venue: nextVenue,
          ...(isEditMode
            ? {}
            : {
                room: "",
                time: "",
                guests: "",
                duration: "",
              }),
        }))

        if (onVenueChange) onVenueChange(Number(nextVenue))
      } catch (err) {
        console.error("Failed to load venues:", err)
      }
    }

    loadBranches()

    return () => {
      cancelled = true
    }
  }, [
    tenantId,
    selectedVenueId,
    onVenueChange,
    bookingData?.venue?.id,
    isEditMode,
  ])

  useEffect(() => {
    if (!selectedVenueId) return
    setForm((prev) => {
      if (isEditMode) {
        return {
          ...prev,
          venue: String(selectedVenueId),
        }
      }

      return {
        ...prev,
        venue: String(selectedVenueId),
        room: "",
        time: "",
        guests: "",
        duration: "",
      }
    })
  }, [selectedVenueId, isEditMode])

  useEffect(() => {
    if (!tenantId || !form.venue) {
      setRooms([])
      return
    }

    setRooms([])

    const loadRooms = async () => {
      try {
        const result = await getActiveRoomsByTenantAndBranch(
          tenantId,
          Number(form.venue),
        )
        const activeRooms = (result || []).filter(
          (room) => room?.is_available !== false,
        )
        const prefilledRoom = hideDetails ? bookingData?.room : null
        const hasPrefilledRoom =
          prefilledRoom &&
          activeRooms.some(
            (room) => Number(room?.id) === Number(prefilledRoom?.id),
          )
        setRooms(
          hasPrefilledRoom
            ? activeRooms
            : prefilledRoom
              ? [...activeRooms, prefilledRoom]
              : activeRooms,
        )
      } catch (err) {
        console.error("Failed to load rooms:", err)
        setRooms([])
      }
    }

    loadRooms()
  }, [tenantId, form.venue, hideDetails, bookingData?.room])

  useEffect(() => {
    if (!tenantId || !form.venue) {
      setSelectedBranchFromApi(null)
      setIsLoadingBranchDetails(false)
      return
    }

    let cancelled = false
    setSelectedBranchFromApi(null)
    setIsLoadingBranchDetails(true)
    setShowVenueLayout(false)

    const loadBranchDetails = async () => {
      try {
        const result = await getSpecificBranchByTenant(
          tenantId,
          Number(form.venue),
        )
        if (cancelled) return
        const branch = Array.isArray(result) ? result[0] : null
        setSelectedBranchFromApi(branch || null)
      } catch (error) {
        console.error("Failed to load branch details:", error)
        if (!cancelled) setSelectedBranchFromApi(null)
      } finally {
        if (!cancelled) setIsLoadingBranchDetails(false)
      }
    }

    loadBranchDetails()

    return () => {
      cancelled = true
    }
  }, [tenantId, form.venue])

  const selectedVenue = useMemo(
    () => branches?.find((branch) => branch.id === Number(form.venue)),
    [branches, form.venue],
  )

  const selectedBranchDetails = useMemo(() => {
    const allBranches = tenant?.data?.branches || []
    const bySelectedVenue = allBranches.find(
      (branch) => Number(branch?.id) === Number(form.venue),
    )
    const prefilledVenue =
      bookingData?.venue && Number(bookingData.venue.id) === Number(form.venue)
        ? bookingData.venue
        : null

    const candidates = [
      selectedBranchFromApi,
      selectedVenue,
      bySelectedVenue,
      prefilledVenue,
      allBranches[0],
    ].filter(Boolean)

    const withDurationSettings = candidates.find(
      (branch) => branch?.duration_settings,
    )

    return withDurationSettings || candidates[0] || null
  }, [
    tenant?.data?.branches,
    form.venue,
    selectedVenue,
    bookingData?.venue,
    selectedBranchFromApi,
  ])

  const selectedRoom = useMemo(
    () => rooms?.find((room) => room.id === Number(form.room)),
    [rooms, form.room],
  )

  const durationSettings = isLoadingBranchDetails
    ? {}
    : selectedBranchFromApi?.duration_settings ||
      selectedBranchDetails?.duration_settings ||
      {}
  const operatingHours = useMemo(
    () => selectedBranchDetails?.operating_hours || {},
    [selectedBranchDetails],
  )

  const bookingLeadTimeHours = Math.max(
    0,
    Number(durationSettings?.booking_lead_time) || 0,
  )
  const bookingIntervalMinutes = Math.max(
    1,
    Number(durationSettings?.booking_interval) || 30,
  )
  const todayStart = useMemo(() => {
    const date = new Date()
    date.setHours(0, 0, 0, 0)
    return date
  }, [])
  const openingBufferMinutes = Math.max(
    0,
    Number(durationSettings?.venue_opening_buffer) || 0,
  )
  const closingBufferMinutes = Math.max(
    0,
    Number(durationSettings?.venue_closing_buffer) || 0,
  )
  const bookingDurationType = String(
    durationSettings?.booking_duration_type || "",
  )
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
  const isFlexibleDurationType = bookingDurationType === "flexible duration"
  const shouldShowDurationField = isFlexibleDurationType
  const durationBasedPricing = Boolean(durationSettings?.duration_based_pricing)
  const fixedDurationMinutes = Math.max(
    0,
    Number(durationSettings?.fixed_duration) || 180,
  )
  const fixedDurationHours = Number((fixedDurationMinutes / 60).toFixed(2))

  const flexMinDurationHours = Math.max(
    0,
    Number(durationSettings?.flex_min_duration) || 0,
  )
  const flexMaxDurationHours = Math.max(
    0,
    Number(durationSettings?.flex_max_duration) || 0,
  )
  const selectedSlotDurationMinutes =
    shouldShowDurationField && form.duration
      ? Math.max(0, Number(form.duration) || 0) * 60
      : fixedDurationMinutes

  useEffect(() => {
    if (skipAutoTimeResetRef.current) {
      skipAutoTimeResetRef.current = false
      return
    }
    setForm((prev) => ({ ...prev, time: "" }))
  }, [form.room, form.date])

  useEffect(() => {
    setBlockedRanges([])

    if (!tenantId || !form.venue || !form.room || !form.date) return

    const iso = toISODate(form.date)
    const mmddyyyy = toMMDDYYYY(form.date)

    let cancelled = false

    ;(async () => {
      try {
        const blocked = await getBlockedDateByTenant(
          tenantId,
          Number(form.venue),
          form.room,
          {
            startDate: iso,
            endDate: iso,
          },
        )

        if (cancelled) return

        const match = (blocked || []).find((x) => x?.date === mmddyyyy)
        const ranges = match ? parseRanges(match.times) : []
        setBlockedRanges(ranges)
      } catch (err) {
        console.error("Failed to load blocked slots:", err)
        if (!cancelled) setBlockedRanges([])
      }
    })()

    return () => {
      cancelled = true
    }
  }, [
    tenantId,
    form.venue,
    form.room,
    form.date,
  ])

  const roomOptions = useMemo(() => {
    if (!rooms || rooms.length === 0) return []
    return (
      rooms?.map((room) => ({
        label: room.name,
        value: room.id,
        secondary: room.capacity ? `max ${room.capacity}` : null,
      })) || []
    )
  }, [rooms])

  const venueOptions = useMemo(() => {
    if (!branches || branches.length === 0) return []
    return (
      branches?.map((branch) => ({
        label: `${branch.branch_name} - ${branch.city}`,
        value: String(branch.id),
      })) || []
    )
  }, [branches])

  const eventOptions = useMemo(() => {
    const branchEventTypes = Array.isArray(selectedBranchDetails?.event_types)
      ? selectedBranchDetails.event_types
      : []
    const tenantTags = Array.isArray(tenant?.data?.tags) ? tenant.data.tags : []
    const merged = [...branchEventTypes, ...tenantTags]
    const unique = [...new Set(merged.filter(Boolean))]

    return unique.map((tag) => ({
      label: tag,
      value: tag,
    }))
  }, [selectedBranchDetails?.event_types, tenant?.data?.tags])
  const dayWindows = useMemo(() => {
    return getCalendarDayBookableWindows(
      operatingHours,
      form.date,
      openingBufferMinutes,
      closingBufferMinutes,
    )
  }, [form.date, operatingHours, openingBufferMinutes, closingBufferMinutes])

  const baseTimeOptions = useMemo(() => {
    return buildTimeOptionsFromWindows(
      dayWindows,
      bookingIntervalMinutes,
      selectedSlotDurationMinutes,
    )
  }, [dayWindows, bookingIntervalMinutes, selectedSlotDurationMinutes])

  const timeOptions = useMemo(() => {
    if (!form.date) return baseTimeOptions

    const now = new Date()
    const leadTimeCutoff = new Date(
      now.getTime() + bookingLeadTimeHours * 60 * 60 * 1000,
    )
    const selectedDate = new Date(form.date)

    const isSelectedDateToday =
      now.toDateString() === selectedDate.toDateString()
    const nowMinutes = now.getHours() * 60 + now.getMinutes()

    return baseTimeOptions.map((opt) => {
      const optMin = hhmmToMinutes(opt.value)
      const optionDateTime = new Date(selectedDate)
      optionDateTime.setHours(Math.floor(optMin / 60), optMin % 60, 0, 0)

      return {
        ...opt,
        disabled:
          isRangeOverlappingBlockedRange(
            optMin,
            selectedSlotDurationMinutes,
            blockedRanges,
          ) ||
          optionDateTime <= leadTimeCutoff ||
          (isSelectedDateToday && optMin <= nowMinutes),
      }
    })
  }, [
    baseTimeOptions,
    blockedRanges,
    form.date,
    bookingLeadTimeHours,
    selectedSlotDurationMinutes,
  ])

  const canDateHaveSlots = (candidateDate) => {
    const candidateWindows = getCalendarDayBookableWindows(
      operatingHours,
      candidateDate,
      openingBufferMinutes,
      closingBufferMinutes,
    )
    if (!candidateWindows?.length) return false

    const now = new Date()
    const leadTimeCutoff = new Date(
      now.getTime() + bookingLeadTimeHours * 60 * 60 * 1000,
    )

    return buildTimeOptionsFromWindows(
      candidateWindows,
      bookingIntervalMinutes,
      selectedSlotDurationMinutes,
    ).some((option) => {
      const optionMin = hhmmToMinutes(option.value)
      const optionDateTime = new Date(candidateDate)
      optionDateTime.setHours(Math.floor(optionMin / 60), optionMin % 60, 0, 0)

      return optionDateTime > leadTimeCutoff
    })
  }

  const durationOptions = useMemo(() => {
    if (!shouldShowDurationField) return []

    if (
      flexMinDurationHours > 0 &&
      flexMaxDurationHours >= flexMinDurationHours
    ) {
      const options = []
      for (
        let hours = flexMinDurationHours;
        hours <= flexMaxDurationHours;
        hours += 1
      ) {
        options.push({
          label: `${hours} hour${hours > 1 ? "s" : ""}`,
          value: String(hours),
        })
      }
      if (options.length) return options
    }

    if (fixedDurationHours > 0) {
      return [
        {
          label: `${fixedDurationHours} hour${fixedDurationHours > 1 ? "s" : ""}`,
          value: String(fixedDurationHours),
        },
      ]
    }

    return [{ label: "3 hours", value: "3" }]
  }, [
    shouldShowDurationField,
    flexMinDurationHours,
    flexMaxDurationHours,
    fixedDurationHours,
  ])

  useEffect(() => {
    if (!shouldShowDurationField) {
      setForm((prev) => ({ ...prev, duration: "" }))
      return
    }

    if (!durationOptions.length) return

    setForm((prev) => {
      if (
        prev.duration &&
        durationOptions.some((opt) => opt.value === prev.duration)
      ) {
        return prev
      }
      return { ...prev, duration: durationOptions[0].value }
    })
  }, [shouldShowDurationField, durationOptions])

  useEffect(() => {
    if (!form.time) return
    if (skipAutoTimeValidationRef.current) {
      skipAutoTimeValidationRef.current = false
      return
    }
    if (
      hideDetails &&
      bookingData?.time &&
      String(form.time) === String(bookingData.time)
    ) {
      return
    }
    const selectedTimeOption = timeOptions.find(
      (opt) => opt.value === form.time,
    )
    if (selectedTimeOption?.disabled) {
      setForm((prev) => ({ ...prev, time: "" }))
    }
  }, [timeOptions, form.time, hideDetails, bookingData?.time])

  useEffect(() => {
    if (!hideDetails || !bookingData || hasAppliedPrefillRef.current) return
    if (!form.venue) return

    const bookingVenueId = bookingData?.venue?.id
    if (
      bookingVenueId !== undefined &&
      bookingVenueId !== null &&
      Number(form.venue) !== Number(bookingVenueId)
    ) {
      return
    }

    const bookingRoomId = bookingData?.roomId || bookingData?.room?.id
    const parsedDate = parseBookingDateValue(
      bookingData?.date,
      bookingData?.month,
      bookingData?.day,
      bookingData?.year,
    )
    const isDateValid =
      parsedDate instanceof Date && !Number.isNaN(parsedDate.getTime())
    const parsedGuests = Number(bookingData?.guests)

    skipAutoTimeResetRef.current = true
    skipAutoTimeValidationRef.current = true
    setForm((prev) => ({
      ...prev,
      room:
        bookingRoomId !== undefined && bookingRoomId !== null
          ? String(bookingRoomId)
          : prev.room,
      date: isDateValid ? parsedDate : prev.date,
      time: bookingData?.time || prev.time,
      guests: Number.isFinite(parsedGuests)
        ? String(parsedGuests)
        : prev.guests,
      eventType:
        bookingData?.eventType && bookingData?.eventType !== "Not specified"
          ? bookingData.eventType
          : prev.eventType,
      duration:
        bookingData?.duration !== undefined && bookingData?.duration !== null
          ? String(bookingData.duration)
          : prev.duration,
    }))
    hasAppliedPrefillRef.current = true
  }, [hideDetails, bookingData, form.venue, rooms])

  const priceRange = tenant?.data?.branches?.[0]?.price_range
  const minPrice = priceRange ? Number(String(priceRange).split("~")[0]) : 0

  const lowestRoomRate = useMemo(() => {
    const rates = (rooms || [])
      .map((room) => Number(room?.rate))
      .filter((rate) => Number.isFinite(rate) && rate > 0)

    if (!rates.length) return null
    return Math.min(...rates)
  }, [rooms])

  const startsAtPrice = lowestRoomRate ?? minPrice

  const selectedBasePrice = selectedRoom?.rate
    ? Number(selectedRoom.rate)
    : startsAtPrice
  const selectedDurationValue = Number(form.duration)
  const selectedPrice =
    durationBasedPricing &&
    isFlexibleDurationType &&
    Number.isFinite(selectedDurationValue) &&
    selectedDurationValue > 0
      ? selectedBasePrice * selectedDurationValue
      : selectedBasePrice

  const guestCapacityExceeded =
    selectedRoom &&
    form.guests &&
    Number(form.guests) > Number(selectedRoom.capacity || 0)

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const handleGuestsChange = (e) => {
    const digitsOnly = String(e.target.value || "").replace(/\D/g, "")
    setForm((prev) => ({ ...prev, guests: digitsOnly }))
  }

  const blockNonNumericKeys = (e) => {
    if (["e", "E", "+", "-", "."].includes(e.key)) e.preventDefault()
  }

  const handleGuestsPaste = (e) => {
    const pasted = e.clipboardData.getData("text")
    if (!/^\d*$/.test(pasted)) e.preventDefault()
  }

  const handleBookNow = () => {
    const tenantImage = tenant?.data?.image_url
    const tenantName = selectedVenue?.branch_name || tenant?.data?.name
    const selectedDate = form.date ? new Date(form.date) : null
    const dayOfWeek = selectedDate
      ? selectedDate.toLocaleDateString("en-US", { weekday: "long" })
      : null

    const day = selectedDate ? selectedDate.getDate() : null
    const month = selectedDate
      ? selectedDate.toLocaleString("en-US", { month: "long" })
      : null
    const year = selectedDate ? selectedDate.getFullYear() : null

    const formattedDate = selectedDate
      ? selectedDate.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : null

    const resolvedDuration =
      shouldShowDurationField && form.duration
        ? Number(form.duration)
        : fixedDurationHours || 3

    const bookingData = {
      tenant: tenant?.data?.name || null,
      venue: selectedBranchDetails || selectedVenue || null,
      payment_settings:
        selectedBranchDetails?.payment_settings ||
        selectedVenue?.payment_settings ||
        null,
      room: selectedRoom,
      roomRate: selectedPrice,
      paymentInstructionsHtml:
        selectedBranchDetails?.payment_instructions ||
        selectedVenue?.payment_instructions ||
        "",
      date: formattedDate,
      dayOfWeek,
      day,
      month,
      year,
      time: form.time,
      guests: Number(form.guests),
      eventType: form.eventType || "Not specified",
      createdAt: new Date().toISOString(),
      duration: resolvedDuration,
      bookingDurationType: isFlexibleDurationType
        ? "Flexible Duration"
        : "Fixed Duration",
    }

    navigate("/booking", {
      state: {
        tenant,
        bookingData,
        tenantName,
        tenantImage,
        rooms,
      },
    })
  }

  const isFormValid =
    form.venue &&
    form.room &&
    form.date &&
    form.time &&
    form.guests &&
    (!shouldShowDurationField || !!form.duration) &&
    !guestCapacityExceeded

  const [openDrawer, setOpenDrawer] = useState(false)
  const isBookingPage = location.pathname === "/booking"

  const desktopCard = (
    <Card sx={{ p: 3, borderRadius: 2, boxShadow: 2 }}>
      <Typography sx={{ mb: 2, color: "text.heading", fontWeight: 700 }}>
        Book this event place
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Venue */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography sx={titleSx}>Venue</Typography>
          <BookingField
            name="venue"
            value={form.venue}
            onChange={(e) => {
              const nextVenueId = e.target.value
              setForm((prev) => ({
                ...prev,
                venue: nextVenueId,
                room: "",
                time: "",
                guests: "",
                duration: "",
              }))
              if (onVenueChange) onVenueChange(Number(nextVenueId))
            }}
            label={createLabel(GreyLocationIcon, "Search for a Venue")}
            options={venueOptions}
            selectProps={{
              renderValue: () =>
                selectedVenue
                  ? `${selectedVenue.branch_name} - ${selectedVenue.city}`
                  : "",
            }}
          />
          {selectedBranchFromApi?.floor_plan && (
            <Button
            type="button"
            onClick={() => setShowVenueLayout(true)}
            sx={{
              alignSelf: "flex-start",
              minWidth: "auto",
              padding: 0,
              mt: 0.3,
              textTransform: "none",
              fontWeight: 600, 
              fontSize: "14px" ,
              color: "text.link.admin",
              textDecoration: "underline",
              "&:hover": {
                backgroundColor: "transparent",
                textDecoration: "underline",
              },
            }}
          >
            View layout ⓘ
          </Button>
          )}
        </Box>

        {/* Room */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography sx={titleSx}>Room</Typography>
          <BookingField
            name="room"
            value={form.room}
            onChange={handleChange}
            label={createLabel(GreyLocationIcon, "Select a Room")}
            options={roomOptions}
            disabled={!form.venue}
            selectProps={{
              renderValue: (selected) =>
                rooms.find((r) => r.id === Number(selected))?.name || "",
            }}
          />
        </Box>

        {/* Date & Time */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography sx={titleSx}>Date & Time</Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Select date"
                value={form.date}
                onChange={(newValue) => setForm({ ...form, date: newValue })}
                disablePast
                minDate={todayStart}
                shouldDisableDate={(date) => !canDateHaveSlots(date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    error: false,
                  },
                  popper: {
                    sx: {
                      "& .MuiPickersDay-root": {
                        color: "text.primary",
                      },
                      "& .MuiPickersDay-root.Mui-selected": {
                        backgroundColor: "text.link.admin",
                        color: "white",
                      },
                      "& .MuiPickersCalendarHeader-label": {
                        color: "text.primary",
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>

            {/* Time */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <BookingField
                label={createLabel(ClockIcon, "Event Time")}
                name="time"
                value={form.time}
                onChange={handleChange}
                options={timeOptions}
                disabled={!selectedRoom || !form.date}
              />

              {shouldShowDurationField && (
                <BookingField
                  label={createLabel(ClockIcon, "Duration")}
                  name="duration"
                  value={form.duration}
                  onChange={handleChange}
                  options={durationOptions}
                  disabled={!selectedRoom || !form.date}
                />
              )}
            </Box>

            <Typography
              sx={{
                fontSize: 14,
                color: "text.subcaption",
              }}
            >
              {shouldShowDurationField
                ? "Select your preferred booking duration."
                : `Fixed ${fixedDurationHours || 3}-hour booking per session.`}
            </Typography>
          </Box>
        </Box>

        {/* Guests */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography sx={titleSx}>Guests</Typography>
          <BookingField
            name="guests"
            value={form.guests}
            onChange={handleGuestsChange}
            onKeyDown={blockNonNumericKeys}
            onPaste={handleGuestsPaste}
            label={createLabel(PersonIcon, "Enter no. of guests")}
            disabled={!selectedRoom}
            error={!!guestCapacityExceeded}
            type="text"
            htmlInputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
          />

          <Typography
            sx={{
              fontSize: 14,
              color: "text.subcaption",
            }}
          >
            {!selectedRoom
              ? "Select a room first to set guest limit."
              : guestCapacityExceeded
                ? `This room can accommodate up to ${selectedRoom.capacity} guests only.`
                : `Max ${selectedRoom.capacity} guests. Contact Venue for larger groups`}
          </Typography>
        </Box>

        {/* Event Type */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography sx={titleSx}>Event Type (Optional)</Typography>
          <BookingField
            name="eventType"
            value={form.eventType}
            onChange={handleChange}
            label={createLabel(BuildingIcon, "Select an Occasion")}
            options={eventOptions}
          />
        </Box>

        {hideDetails && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1,
              width: "100%",
            }}
          >
            <Button
              variant="outlined"
              fullWidth
              size="medium"
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                fontWeight: 600,
                color: "text.primary",
                borderColor: "grey.400",
                "&:hover": {
                  borderColor: "text.primary",
                },
              }}
              onClick={onClose}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              fullWidth
              size="medium"
              disabled={!isFormValid}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                fontWeight: 600,
              }}
              onClick={() => {
                const selectedDate = form.date ? new Date(form.date) : null
                const dayOfWeek = selectedDate
                  ? selectedDate.toLocaleDateString("en-US", {
                      weekday: "long",
                    })
                  : null
                const formattedDate = selectedDate
                  ? selectedDate.toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })
                  : null

                const day = selectedDate ? selectedDate.getDate() : null

                const updatedBookingData = {
                  room: selectedRoom || null,
                  venue: selectedBranchDetails || selectedVenue || null,
                  payment_settings:
                    selectedBranchDetails?.payment_settings ||
                    selectedVenue?.payment_settings ||
                    null,
                  paymentInstructionsHtml:
                    selectedBranchDetails?.payment_instructions ||
                    selectedVenue?.payment_instructions ||
                    "",
                  roomId: selectedRoom?.id || null,
                  roomRate: selectedPrice,
                  date: formattedDate,
                  dayOfWeek,
                  day,
                  time: form.time,
                  guests: Number(form.guests),
                  eventType: form.eventType || "Not specified",
                  duration:
                    shouldShowDurationField && form.duration
                      ? Number(form.duration)
                      : fixedDurationHours || 3,
                  bookingDurationType: isFlexibleDurationType
                    ? "Flexible Duration"
                    : "Fixed Duration",
                }

                onSave(updatedBookingData)
                onClose()
              }}
            >
              Save
            </Button>
          </Box>
        )}

        {/* Price + CTA */}
        {!hideDetails && (
          <Box sx={{ display: "flex", flexDirection: "column", mt: 1 }}>
            <Typography
              sx={{
                textAlign: "right",
                color: "text.subcaption",
                fontWeight: 500,
                fontSize: "14px",
              }}
            >
              Price starts at{" "}
              <Typography
                component="span"
                sx={{
                  fontWeight: 700,
                  color: "text.heading",
                  fontSize: "20px",
                }}
              >
                ₱{selectedPrice.toLocaleString()}
              </Typography>
            </Typography>

            <Button
              variant="contained"
              fullWidth
              disabled={!isFormValid}
              sx={{
                mt: 1.5,
                py: 1.3,
                bgcolor: isFormValid ? "primary.main" : "grey.400",
                fontWeight: 600,
                "&:hover": {
                  bgcolor: isFormValid ? "primary.dark" : "grey.400",
                },
              }}
              onClick={handleBookNow}
            >
              {isMobile ? "Check Date and Time" : "Book Now"}
            </Button>

            <Typography
              variant="caption"
              display="block"
              sx={{
                mt: 3,
                textAlign: "center",
                color: "text.caption",
                fontSize: "14px",
                fontWeight: 400,
              }}
            >
              You won't be charged yet. Payment for this venue is currently
              accepted{" "}
              <Typography
                component="span"
                sx={{ fontWeight: 700, color: "text.primary" }}
              >
                in-store only.
              </Typography>
            </Typography>
          </Box>
        )}
      </Box>

      {showVenueLayout && selectedBranchFromApi?.floor_plan && (
      <Box
        onClick={() => setShowVenueLayout(false)}
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          backgroundColor: "rgba(0, 0, 0, 0.65)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Box
          onClick={(event) => event.stopPropagation()}
          sx={{
            position: "relative",
            backgroundColor: "#fff",
            width: {
              xs: "95%",
              sm: "90%",
              md: "80%",
              lg: "70%",
            },
            maxWidth: "900px",
            maxHeight: "90vh",
            p: {
              xs: 1,
              sm: 2,
            },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "2px",
          }}
        >
          {/* Close button */}
          <Button
            type="button"
            onClick={() => setShowVenueLayout(false)}
            sx={{
              position: "absolute",
              top: -14,
              right: -14,
              minWidth: 30,
              width: 30,
              height: 30,
              padding: 0,
              borderRadius: "50%",
              backgroundColor: "#fff",
              color: "#333",
              fontSize: "20px",
              lineHeight: 1,
              zIndex: 2,
              boxShadow: "0 1px 5px rgba(0,0,0,0.25)",
              "&:hover": {
                backgroundColor: "#fff",
              },
            }}
          >
            ×
          </Button>

          <Box
            component="img"
            src={getFullImageUrl(selectedBranchFromApi.floor_plan)}
            alt="Venue layout"
            sx={{
              display: "block",
              maxWidth: "100%",
              maxHeight: "calc(90vh - 32px)",
              width: "auto",
              height: "auto",
              objectFit: "contain",
            }}
          />
        </Box>
      </Box>
    )}
    </Card>
  )

  if (!isMobile) return desktopCard

  if (isMobile) {
    if (isBookingPage) {
      return <Box>{desktopCard}</Box>
    }

    return (
      <>
        <Card
          sx={{
            p: 2.5,
            borderRadius: "16px 16px 0 0",
            boxShadow: "0 -4px 15px rgba(0, 0, 0, 0.1)",
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: "background.paper",
            zIndex: 1200,
            pb: "calc(env(safe-area-inset-bottom) + 12px)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              sx={{
                color: "text.heading",
                fontWeight: 600,
                fontSize: "18px",
              }}
            >
              Book event place
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
              }}
            >
              <Typography
                sx={{
                  color: "text.subcaption",
                  fontWeight: 500,
                  fontSize: "14px",
                }}
              >
                Price starts at
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontWeight: 700,
                  fontSize: "18px",
                  color: "text.heading",
                }}
              >
                ₱{selectedPrice.toLocaleString()}
              </Typography>
            </Box>
          </Box>

          <Button
            variant="contained"
            fullWidth
            sx={{
              py: 1.3,
              fontWeight: 600,
              bgcolor: "primary.main",
              "&:hover": { bgcolor: "#e62e5c" },
              borderRadius: "12px",
            }}
            onClick={() => setOpenDrawer(true)}
          >
            Check Date and Time
          </Button>

          <Typography
            variant="caption"
            display="block"
            sx={{
              mt: 2,
              textAlign: "center",
              color: "text.primary",
              fontSize: "13px",
              fontWeight: 400,
            }}
          >
            You won't be charged yet. <br /> Payment is accepted{" "}
            <Typography
              component="span"
              sx={{ fontWeight: 700, color: "text.primary" }}
            >
              in-store only.
            </Typography>
          </Typography>
        </Card>

        <Drawer
          anchor="bottom"
          open={openDrawer}
          onClose={() => setOpenDrawer(false)}
          slotProps={{
            paper: {
              sx: {
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                height: "90vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              },
            },
          }}
        >
          <Box sx={{ flex: 1, overflowY: "auto" }}>{desktopCard}</Box>
        </Drawer>
      </>
    )
  }

  return null
}
